import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import dayjs from 'dayjs';
import { FinnishTimeFormat } from '../../utils/dateHelpers';
import { OrderV2 } from '@kotipizzagroup/kotipizza-orders-api-client';
import { StyledPaper, TitleRow } from '../../Styled/Styled';
import { CustomerDetailsContainer } from './Styled';
import styled from 'styled-components';
import { TimelineOppositeContent } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { getOrderPaymentState, OrderPaymentState } from '../../utils/orderUtils';
import { DELIVERY_TYPES } from '../constants';

export const OrderTimeLine: React.FC<{ order: OrderV2 }> = ({ order }) => {
  const { t } = useTranslation();
  const orderPaymentState = getOrderPaymentState(order);
  // Deliberately not the shared `isOrderPaid`, which counts a late payment as paid: here the late
  // case has an entry of its own below, so this one has to exclude it.
  const hasPaidOnTime =
    orderPaymentState === OrderPaymentState.Paid || orderPaymentState === OrderPaymentState.PaidInRestaurant;
  const isOrderPaidTooLate = orderPaymentState === OrderPaymentState.PaymentReceivedTooLate;

  return (
    <StyledPaper variant="outlined">
      <CustomerDetailsContainer>
        <TitleRow>
          <h3>{t('Timeline')}</h3>
        </TitleRow>
        <StyledTimeline>
          {order.orderPlaced != null && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="success" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.orderPlaced).format(FinnishTimeFormat)}</EntryTime> {t('Payment initiated')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {hasPaidOnTime && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="success" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.orderPaid).format(FinnishTimeFormat)}</EntryTime> {t('Paid')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {isOrderPaidTooLate && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="error" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.orderPaid).format(FinnishTimeFormat)}</EntryTime>{' '}
                {t('Payment received too late')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.RegisterDeliveredTime != null && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="success" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.RegisterDeliveredTime).format(FinnishTimeFormat)}</EntryTime>{' '}
                {t('Send to the register')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.RegisterAcceptedTime != null && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="success" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.RegisterAcceptedTime).format(FinnishTimeFormat)}</EntryTime>{' '}
                {t('Accepted from the register')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.RegisterManufacturingTime != null && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="success" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.RegisterManufacturingTime).format(FinnishTimeFormat)}</EntryTime>{' '}
                {t('To the oven')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.ReadyForDispatch != null && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="success" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.ReadyForDispatch).format(FinnishTimeFormat)}</EntryTime> {t('Packed')}{' '}
                {order.deliveryType !== DELIVERY_TYPES.delivery ? `(${t('Done')})` : ''}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.s4dDeliveryStartedTimestamp != null && (
            <StyledTimelineItem>
              <TimelineSeparator>
                <StyledTimelineDot color="success" />
                <StyledTimelineConnector />
              </TimelineSeparator>
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.s4dDeliveryStartedTimestamp).format(FinnishTimeFormat)}</EntryTime>{' '}
                {t('In delivery')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.s4dDeliveryStartedTimestamp === null &&
            order.s4dOrderId != null &&
            order.ReadyForDeliveryTime != null && (
              <StyledTimelineItem>
                <TimelineSeparator>
                  <StyledTimelineDot color="grey" />
                  <StyledTimelineConnector />
                </TimelineSeparator>
                <TimelineOppositeContent display="none" />
                <TimelineContent>{t('Delivery timestamp not found')}</TimelineContent>
              </StyledTimelineItem>
            )}
          {order.s4dDeliveryCompleteTimestamp != null && (
            <StyledTimelineItem>
              <StyledTimelineDot color="success" />
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.s4dDeliveryCompleteTimestamp).format(FinnishTimeFormat)}</EntryTime>{' '}
                {t(order.deliveryType === DELIVERY_TYPES.delivery ? 'Done' : 'Finalized')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.s4dOrderId === null && order.ProcessFinished != null && (
            <StyledTimelineItem>
              <StyledTimelineDot color="success" />
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.ProcessFinished).format(FinnishTimeFormat)}</EntryTime> {t('Done')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
          {order.OrderCancelledTime && (
            <StyledTimelineItem>
              <StyledTimelineDot color="error" />
              <TimelineOppositeContent display="none" />
              <TimelineContent>
                <EntryTime>{dayjs(order.OrderCancelledTime).format(FinnishTimeFormat)}</EntryTime> {t('Cancelled')}
              </TimelineContent>
            </StyledTimelineItem>
          )}
        </StyledTimeline>
      </CustomerDetailsContainer>
    </StyledPaper>
  );
};

// MUI pads the timeline vertically on top of the 11.5px top margin it gives every dot, which left
// the first entry sitting lower under its heading than the other cards' first row does. Only the
// horizontal padding is wanted here.
const StyledTimeline = styled(Timeline)`
  && {
    padding: 0;
    /* MUI gives every dot an 11.5px vertical margin. Between two dots that is the spacing; above the
       first and below the last it is space with nothing on the other side of it. Pulled back on the
       block rather than on those two dots, so every dot stays level with its own text. */
    margin: -11.5px 0;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    && {
      padding: 0 16px;
    }
  }

  /* A connector joins one dot to the next, and the last dot has nothing to join to. Without it the
     item no longer needs the height it was reserving to draw the line in. */
  .MuiTimelineItem-root:last-child {
    min-height: 0;

    .MuiTimelineConnector-root {
      display: none;
    }
  }
`;

// The clock reading and what happened are two facts, not one phrase.
const EntryTime = styled.span`
  margin-right: 8px;
`;

const StyledTimelineConnector = styled(TimelineConnector)`
  background-color: ${({ theme }) => theme.palette.primary.main};
  width: 3px;
  margin-top: -12px;
  margin-bottom: -12px;
`;

const StyledTimelineDot = styled(TimelineDot)`
  padding: 5px;
`;

const StyledTimelineItem = styled(TimelineItem)`
  min-height: 40px;
`;
